import { useMutation } from '@apollo/client'
import { Button, Code, FormModal, Link, ListItem, Modal, OrderedList, Text, useDisclosure } from '@chaine/keychaine'
import { encode } from 'base-64'
import React from 'react'
import { SubmitHandler } from 'react-hook-form'

import { Toast } from '../../../_shared/components/toast'
import { validations } from '../../../_utils/validations/validations'
import { AppModalProps } from '../components/apps-data'
import {
  ENABLE_PROJECT44_APP,
  EnableProject44AppInput,
  SAVE_PROJECT44_CREDENTIALS,
  SaveEncodedCredentialsInput
} from '../data/mutations'

/**
 * Save's project44 apps encoded credentials.
 * @param props.app the app
 * @param props.isOpen sets the modal to open or closed
 * @param props.onClose closes the modal
 */
const Project44Modal = React.memo((props: AppModalProps) => {
  const { isOpen, onClose, appType, refetchApps } = props
  const { isOpen: isStep2Open, onOpen: onStep2Open, onClose: onStep2Close } = useDisclosure()

  // TODO: use this variable to only show the second step
  //const encodedCredentialsValidated: boolean = workspaceApp?.appMetadata?.encodedCredentialsValidated ?? false

  const { showToast } = Toast()

  const [saveProject44AppCredentials, { loading }] = useMutation<
    { saveProject44AppCredentials: { id: string } },
    SaveEncodedCredentialsInput
  >(SAVE_PROJECT44_CREDENTIALS, {
    onCompleted: () => {
      onStep2Open()
    },
    onError: (err) => {
      if (err.message === 'InvalidCredentials') {
        showToast({
          status: 'error',
          title: `Invalid Project44 credentials. Please try again.`
        })
      } else {
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  const [enableProject44App, { loading: enableAppLoading }] = useMutation<
    { saveProject44AppCredentials: { id: string } },
    EnableProject44AppInput
  >(ENABLE_PROJECT44_APP, {
    onCompleted: () => {
      refetchApps()
      onStep2Close()
      onClose()
    },
    onError: (err) => {
      if (err.message === 'InvalidScopes') {
        showToast({
          status: 'error',
          title: `Invalid Project44 scopes. Please check the documentation below. If error persists, email support at support@chaineapp.com`
        })
      } else {
        showToast({
          status: 'error',
          title: `Something went wrong. Please contact support@chaineapp.com. Error: ${err.message}`
        })
      }
    }
  })

  type FieldValues = { appPassword?: string; appUsername?: string }
  const handleSaveDetails: SubmitHandler<FieldValues> = (event) => {
    const usernamePasswordString = `${event.appUsername}:${event.appPassword}`
    const base64EncodedCredentials = encode(usernamePasswordString)

    saveProject44AppCredentials({
      variables: { appType: appType, encodedUsernamePassword: base64EncodedCredentials }
    })
  }

  const handleConfirmEnable = () => {
    enableProject44App({
      variables: { appType: appType }
    })
  }

  return (
    <>
      <FormModal
        isOpen={isOpen}
        onClose={onClose}
        title={`Enable ${appType} for Chaine`}
        subtitle={`Enabling this integration requires an Admin project44 account. Enter your Admin project44 username and password.`}
        formSubmitHandler={handleSaveDetails}
        buttonText={'Enable'}
        initialValues={{}}
        validations={validations}
        isLoading={loading}
        modalItems={[
          {
            labelText: 'project44 admin username',
            name: 'appUsername',

            placeHolder: 'Enter your project44 username',
            type: 'input'
          },
          {
            labelText: 'project44 admin password',
            name: 'appPassword',

            placeHolder: 'Enter the client secret',
            type: 'input'
          }
        ]}
      />

      <Modal
        isOpen={isStep2Open}
        onClose={onStep2Close}
        title={`One more step quick!`}
        subtitle={'Project 44 requires that you give us permission.'}
        footer={
          <>
            <Button isLoading={enableAppLoading} variant={'primary'} mr={3} onClick={handleConfirmEnable}>
              Confirm setup
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </>
        }
      >
        <>
          <Text>
            {`See `}
            <Link
              color="blue.600"
              href="https://handbook.chaineapp.com/product-guide/apps/project44/getting-started#1-go-to-user-manage-inside-of-project44"
              target="_blank"
              rel="noopener noreferrer"
            >
              detailed steps here
            </Link>
          </Text>
          <OrderedList pt={4}>
            <ListItem>Go to user-management inside of project44</ListItem>
            <ListItem>Find the newly created Chaine user (search for chaine-app), and click edit</ListItem>
            <ListItem>
              Add <Code>Customer Admin</Code>, <Code>Truckload Visibility API</Code>, and <Code>Developer</Code>, roles
            </ListItem>
            <ListItem>Make sure to click SAVE at the top right</ListItem>
          </OrderedList>
          <Text pt={4}>{"Once you're done, click the button below to check if it was setup correctly"}</Text>
        </>
      </Modal>
    </>
  )
})

Project44Modal.displayName = 'Project44Modal'

export { Project44Modal }
